import {
  Integer,
  ThemeIconKey,
  Box,
  Center,
  Hide,
  HStack,
  Icon,
  Show,
  Spinner,
  TextSize,
} from 'platform/foundation';
import {cn, cva} from 'platform/shared';

import {suffixTestId} from 'shared/web';

import {BaseFlagProps} from '../Flag/BaseFlagProps';
import {FlagLabel} from '../Flag/components/FlagLabel';
import {getColorsFromScheme} from '../Flag/getColorsFromScheme';
import {getFlagElementSizes} from '../Flag/getFlagElementSizes';
import {TagHoverBackgroundColor} from '../Tag/Tag';

interface DropdownControlProps extends BaseFlagProps {
  isOpen: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const dropdownControlVariants = cva(
  'w-fit focus-visible:[outline:var(--outline-default)_var(--color-general-accent)] focus-visible:[outline-offset:var(--outline-offset)]',
  {
    variants: {
      borderRadius: {
        small: 'rounded-[var(--radius-small)]',
        xSmall: 'rounded-[var(--radius-x-small)]',
      },
    },
  }
);

export function DropdownControl(props: DropdownControlProps) {
  const {height, labelHorizontalPadding, borderRadius} = getFlagElementSizes(props.size);
  const textSize: TextSize = props.size === 'small' ? 'xxSmall' : 'small';
  const iconSize: Integer = props.size === 'small' ? 3 : 4;
  const iconWrapperWidth = height;
  const {backgroundColor, color, hoverColor} = getColorsFromScheme(
    props.colorScheme,
    props.isSubtle
  );

  const iconPath: ThemeIconKey = props.isOpen ? 'navigation/expand_less' : 'navigation/expand_more';

  return (
    <div
      className={cn(dropdownControlVariants({borderRadius}))}
      role="button"
      aria-label="Toggle dropdown"
      aria-expanded={props.isOpen}
      tabIndex={0}
    >
      <Box
        borderRadius={borderRadius}
        height={height}
        backgroundColor={backgroundColor}
        overflow="hidden"
        position="relative"
        data-testid={suffixTestId('DropdownControl', props)}
      >
        <TagHoverBackgroundColor
          $hoverColorPath={props.isDisabled ? 'general.transparent' : hoverColor}
        >
          <HStack justify="space-between" align="center" height="100%">
            <Box
              paddingHorizontal={labelHorizontalPadding}
              data-testid={suffixTestId('DropdownControl-label', props)}
            >
              <FlagLabel label={props.label} size={textSize} color={color} />
            </Box>

            <Show when={props.isLoading}>
              <Spinner size="small" variant="overlay" />
            </Show>

            <Hide when={props.isDisabled}>
              <Center height="100%" width={iconWrapperWidth}>
                <Icon
                  value={iconPath}
                  size={iconSize}
                  color={color}
                  data-testid={suffixTestId('DropdownControl-icon', props)}
                />
              </Center>
            </Hide>
          </HStack>
        </TagHoverBackgroundColor>
      </Box>
    </div>
  );
}
